import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Car,
  MapPin,
  Clock,
  Users,
  DollarSign,
  Calendar,
  Search,
  Plus,
  ChevronRight,
  Shield,
  GraduationCap,
  Briefcase,
  RefreshCw,
  Star,
  CheckCircle
} from 'lucide-react';

const routeTypeConfig = {
  school: { label: 'School Run', icon: GraduationCap, color: 'bg-plp-blue' },
  work: { label: 'Work Commute', icon: Briefcase, color: 'bg-plp-gold text-plp-blue' },
  general: { label: 'General', icon: Car, color: 'bg-plp-blue-light' }
};

const contributionLabels = {
  fuel: 'Fuel Contribution',
  fixed: 'Fixed Amount',
  negotiable: 'Negotiable',
  free: 'Free Ride'
};

const CarpoolPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState('all');
  const [myDriverProfile, setMyDriverProfile] = useState(null);

  useEffect(() => {
    fetchRoutes();
    if (user && token) {
      fetchMyDriverProfile();
    }
  }, [user, token]);

  const fetchRoutes = async () => {
    try {
      const params = activeType !== 'all' ? { route_type: activeType } : {};
      const response = await axios.get(`${API}/carpool/routes`, { params });
      setRoutes(response.data);
    } catch (error) {
      console.error('Failed to fetch routes:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyDriverProfile = async () => {
    try {
      const response = await axios.get(`${API}/carpool/my-driver-profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyDriverProfile(response.data);
    } catch (error) {
      // Not a driver yet
    }
  };

  useEffect(() => {
    fetchRoutes();
  }, [activeType]);

  const filteredRoutes = routes.filter(r =>
    r.route_name.toLowerCase().includes(search.toLowerCase()) ||
    r.start_location.toLowerCase().includes(search.toLowerCase()) ||
    r.end_location.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="bg-gradient-to-r from-plp-blue to-plp-blue-light py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-16 h-16 bg-plp-gold rounded-2xl flex items-center justify-center">
              <Car className="w-8 h-8 text-plp-blue" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                Killarney Rides
              </h1>
              <p className="text-white/80">Community Carpool Program</p>
            </div>
          </div>
          <p className="text-white/80 max-w-2xl mb-6">
            Connect with neighbors for school runs, work commutes, and more. 
            Save money, reduce traffic, and build community.
          </p>

          {/* Search */}
          <div className="flex flex-col sm:flex-row gap-4 max-w-2xl">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search routes, locations..."
                className="pl-12 py-6 rounded-xl bg-white/10 border-white/20 text-white placeholder:text-white/50 focus:bg-white focus:text-slate-900"
                data-testid="carpool-search"
              />
            </div>
            {user ? (
              <div className="flex gap-2">
                {myDriverProfile?.status === 'approved' ? (
                  <Link to="/carpool/my-routes">
                    <Button className="bg-plp-gold text-plp-blue hover:bg-plp-gold-light px-6 py-6">
                      <Car className="w-5 h-5 mr-2" /> My Routes
                    </Button>
                  </Link>
                ) : myDriverProfile ? (
                  <Button disabled className="bg-white/50 text-plp-blue px-6 py-6">
                    <Clock className="w-5 h-5 mr-2" /> Application Pending
                  </Button>
                ) : (
                  <Link to="/carpool/become-driver">
                    <Button className="bg-plp-gold text-plp-blue hover:bg-plp-gold-light px-6 py-6">
                      <Plus className="w-5 h-5 mr-2" /> Become a Driver
                    </Button>
                  </Link>
                )}
              </div>
            ) : (
              <Link to="/auth">
                <Button className="bg-plp-gold text-plp-blue hover:bg-plp-gold-light px-6 py-6">
                  Sign In to Join
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* How It Works */}
        <Card className="bg-plp-gold/10 border-plp-gold/30 mb-8">
          <CardContent className="p-6">
            <h3 className="font-heading font-bold text-lg text-slate-900 mb-4">How Killarney Rides Works</h3>
            <div className="grid md:grid-cols-4 gap-4">
              {[
                { icon: Shield, title: 'Vetted Drivers', desc: 'All drivers are verified by admin' },
                { icon: Calendar, title: 'Flexible Schedule', desc: 'Choose routes that fit your schedule' },
                { icon: Users, title: 'Community Chat', desc: 'Coordinate pickup times & locations' },
                { icon: DollarSign, title: 'Fair Contributions', desc: 'Agree on fuel/costs upfront' }
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-plp-blue rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{item.title}</p>
                    <p className="text-sm text-slate-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Route Type Tabs */}
        <Tabs value={activeType} onValueChange={setActiveType} className="space-y-6">
          <TabsList className="bg-white border flex-wrap h-auto p-1">
            <TabsTrigger value="all" data-testid="tab-all-routes" className="text-xs sm:text-sm">All Routes</TabsTrigger>
            <TabsTrigger value="school" data-testid="tab-school" className="text-xs sm:text-sm">
              <GraduationCap className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">School Runs</span><span className="sm:hidden">School</span>
            </TabsTrigger>
            <TabsTrigger value="work" data-testid="tab-work" className="text-xs sm:text-sm">
              <Briefcase className="w-4 h-4 mr-1 sm:mr-2" /> <span className="hidden sm:inline">Work Commutes</span><span className="sm:hidden">Work</span>
            </TabsTrigger>
            <TabsTrigger value="general" data-testid="tab-general" className="text-xs sm:text-sm">
              <Car className="w-4 h-4 mr-1 sm:mr-2" /> General
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeType}>
            {loading ? (
              <div className="flex justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-emerald-600" />
              </div>
            ) : filteredRoutes.length === 0 ? (
              <Card className="bg-white">
                <CardContent className="py-12 text-center">
                  <Car className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No carpool routes available</p>
                  <p className="text-sm text-slate-400 mt-2">Be the first to offer a ride!</p>
                  {user && (
                    <Link to="/carpool/become-driver">
                      <Button className="mt-4 bg-emerald-600 hover:bg-emerald-700">
                        <Plus className="w-4 h-4 mr-2" /> Become a Driver
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredRoutes.map((route) => {
                  const typeConfig = routeTypeConfig[route.route_type] || routeTypeConfig.general;
                  const spotsLeft = route.max_riders - route.current_riders;
                  
                  return (
                    <Link key={route.id} to={`/carpool/route/${route.id}`} data-testid={`route-card-${route.id}`}>
                      <Card className="bg-white h-full hover:shadow-lg transition-all hover:-translate-y-1">
                        <CardContent className="p-0">
                          <div className={`${typeConfig.color} p-4 text-white`}>
                            <div className="flex items-center justify-between">
                              <Badge className="bg-white/20 text-white">
                                <typeConfig.icon className="w-3 h-3 mr-1" />
                                {typeConfig.label}
                              </Badge>
                              {spotsLeft > 0 ? (
                                <Badge className="bg-white text-emerald-600">{spotsLeft} spots left</Badge>
                              ) : (
                                <Badge className="bg-red-500 text-white">Full</Badge>
                              )}
                            </div>
                            <h3 className="font-heading font-bold text-lg mt-3">{route.route_name}</h3>
                          </div>
                          
                          <div className="p-4">
                            <div className="space-y-2 text-sm">
                              <div className="flex items-start gap-2">
                                <MapPin className="w-4 h-4 text-emerald-500 mt-0.5" />
                                <div>
                                  <p className="text-slate-600">From: {route.start_location}</p>
                                  <p className="text-slate-600">To: {route.end_location}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-600">{route.departure_time}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <span className="text-slate-600">{route.schedule_days?.join(', ')}</span>
                              </div>
                            </div>
                            
                            {/* Driver Info */}
                            <div className="mt-4 pt-4 border-t flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-plp-gold rounded-full flex items-center justify-center">
                                  <span className="text-sm font-bold text-plp-blue">
                                    {route.driver?.name?.charAt(0)}
                                  </span>
                                </div>
                                <div>
                                  <p className="text-sm font-medium">{route.driver?.name}</p>
                                  <p className="text-xs text-slate-500">
                                    {route.driver_profile?.vehicle_make} {route.driver_profile?.vehicle_model}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="outline" className="text-xs">
                                {contributionLabels[route.contribution_type]}
                              </Badge>
                            </div>
                            
                            <div className="mt-4 flex items-center text-emerald-600 text-sm font-medium">
                              View Details <ChevronRight className="w-4 h-4 ml-1" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* User Quick Links */}
        {user && (
          <div className="mt-8 grid md:grid-cols-2 gap-4">
            <Link to="/carpool/my-rides">
              <Card className="bg-white hover:shadow-md transition-shadow">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading font-semibold">My Ride Requests</h3>
                    <p className="text-sm text-slate-500">View your pending and approved rides</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400" />
                </CardContent>
              </Card>
            </Link>
            
            {myDriverProfile?.status === 'approved' && (
              <Link to="/carpool/my-routes">
                <Card className="bg-white hover:shadow-md transition-shadow">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Car className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading font-semibold">My Routes</h3>
                      <p className="text-sm text-slate-500">Manage your carpool routes & riders</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </CardContent>
                </Card>
              </Link>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CarpoolPage;
